import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  deleteEmailDraft,
  EmailReplyDocument,
  getEmailDraftStatus,
  getEmailReplyDocuments,
  postDraftWithAi,
  postEmailThreadSeen,
  postNativeEmailReply,
  putEmailDraft,
  toReplyFailure,
} from '@/apis/emailReply';
import type { EmailDraftAI, EmailPanelNative } from '@/apis/emailReplyContract';
import type { PlaceholderEditorHandle } from './PlaceholderEditor';
import type { EmailReplyData } from '@/apis/shipment';

import {
  countPlaceholders,
  firstPlaceholder,
  removeFirstNotAttachedYet,
} from './placeholders';

/** Total size of one reply's attachments. ZOO-694 enforces its own limit too. */
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
export const AUTOSAVE_DELAY_MS = 1500;
export const DRAFT_STATUS_POLL_MS = 3000;

const NO_AI_DRAFT: EmailDraftAI = {
  status: 'none',
  body: null,
  generated_at: null,
};

// Background calls (autosave, seen, polling) fail quietly; the next one retries.
const ignoreFailure = (): void => undefined;

const draftKey = (body: string, ids: string[]): string =>
  `${body}\u0000${ids.join(',')}`;

const newId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/** Why attaching failed; the view words it in the user's language. */
export type AttachError = { kind: 'limit'; mb: number } | { kind: 'documents' };

/** A reply as the user sees it between Send and the next reload of the thread. */
export interface OutboxItem {
  localId: string;
  /** Reused on Retry, so a second try cannot send twice. */
  clientRequestId: string;
  body: string;
  files: File[];
  documentIds: string[];
  attachmentNames: string[];
  placeholdersSent: number;
  /** What the box was pre-filled with when this reply was sent ('' if nothing). */
  aiDraftText: string;
  status: 'sending' | 'failed' | 'sent';
  error?: string;
  /** What the server answered once it accepted the reply. */
  reply?: EmailReplyData;
}

interface Args {
  token: string;
  /** The ShipmentAlert id (not a comment id); the reply endpoints live under it. */
  alertId: string | undefined;
  native: EmailPanelNative | undefined;
  /** The user clicked Reply on an entry: open with the composer expanded. */
  openComposer: boolean;
  /** Reload the thread without clearing it, so nothing typed is lost. */
  reloadTranscript: () => Promise<void>;
}

/**
 * All state of the reply composer. It lives next to, and never inside, the
 * transcript: the panel clears its transcript on every reload and on the
 * language toggle, and none of that may wipe what the user is typing.
 */
export function useEmailComposer({
  token,
  alertId,
  native,
  openComposer,
  reloadTranscript,
}: Args) {
  const threadId = native?.reply_target_thread?.id ?? null;
  const canReply = Boolean(native?.can_reply && threadId && alertId);

  const [body, setBodyState] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [documentIds, setDocumentIds] = useState<string[]>([]);
  const [catalog, setCatalog] = useState<EmailReplyDocument[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [aiDraft, setAiDraft] = useState<EmailDraftAI>(NO_AI_DRAFT);
  // The AI text that was put in the box, to tell "edited" from "untouched".
  const [appliedAiBody, setAppliedAiBody] = useState('');
  // The AI draft goes into the box only when the user asked for it: by clicking
  // Reply on a CoC entry (open_composer) or by pressing Draft with AI. Opening
  // the thread with View email transcript leaves the box empty.
  const [wantAiDraft, setWantAiDraft] = useState(false);
  // Draft with AI was pressed (so a failure or an empty answer is shown).
  const [aiRequested, setAiRequested] = useState(false);
  const [aiRequestFailed, setAiRequestFailed] = useState(false);
  const [outbox, setOutbox] = useState<OutboxItem[]>([]);
  const [conflict, setConflict] = useState<string | null>(null);
  const [guardCount, setGuardCount] = useState<number | null>(null);
  const [attachError, setAttachError] = useState<AttachError | null>(null);

  // Once the user has typed, a late AI draft is never applied.
  const touchedRef = useRef(false);
  const savedDraftRef = useRef(false);
  const initializedFor = useRef<string | null>(null);
  const seenFor = useRef<string | null>(null);
  const editorRef = useRef<PlaceholderEditorHandle>(null);
  const nativeRef = useRef(native);
  nativeRef.current = native;

  // ---- start of a thread ----------------------------------------------------

  useEffect(() => {
    if (!canReply || !threadId) return;
    if (initializedFor.current === threadId) return;
    initializedFor.current = threadId;

    const saved = native?.draft?.user ?? null;
    const ai = native?.draft?.ai ?? NO_AI_DRAFT;
    setAiDraft(ai);
    touchedRef.current = Boolean(saved);
    savedDraftRef.current = Boolean(saved);
    lastSavedKey.current = saved
      ? draftKey(saved.body, saved.attachment_ids)
      : null;
    setOutbox([]);
    setConflict(null);
    setGuardCount(null);
    setFiles([]);
    setAiRequested(false);
    setAiRequestFailed(false);
    setWantAiDraft(openComposer && !saved);
    if (saved) {
      setBodyState(saved.body);
      setDocumentIds(saved.attachment_ids);
      setAppliedAiBody('');
      setExpanded(true);
    } else {
      setBodyState('');
      setDocumentIds([]);
      setAppliedAiBody('');
      setExpanded(openComposer);
    }
  }, [canReply, threadId, native, openComposer]);

  // A reload of the same thread may bring a newer AI draft state.
  const incomingAi = native?.draft?.ai;
  useEffect(() => {
    if (initializedFor.current !== threadId || !incomingAi) return;
    setAiDraft((prev) =>
      prev.status === incomingAi.status && prev.body === incomingAi.body
        ? prev
        : incomingAi
    );
  }, [incomingAi, threadId]);

  // ---- the user opened the thread ------------------------------------------

  useEffect(() => {
    if (!native || !threadId || !alertId) return;
    const key = `${alertId}:${threadId}`;
    if (seenFor.current === key) return;
    seenFor.current = key;
    postEmailThreadSeen(token, alertId).catch(ignoreFailure);
  }, [native, threadId, alertId, token]);

  // ---- AI draft: loading, then pre-fill -------------------------------------

  useEffect(() => {
    if (!canReply || !alertId || !wantAiDraft || aiDraft.status !== 'pending') {
      return;
    }
    const timer = setInterval(() => {
      getEmailDraftStatus(token, alertId).then(setAiDraft).catch(ignoreFailure);
    }, DRAFT_STATUS_POLL_MS);
    return () => clearInterval(timer);
  }, [canReply, alertId, token, wantAiDraft, aiDraft.status]);

  // A wanted draft goes into the box, but never over what the user typed.
  useEffect(() => {
    if (
      !wantAiDraft ||
      touchedRef.current ||
      aiDraft.status !== 'ready' ||
      !aiDraft.body
    ) {
      return;
    }
    setBodyState(aiDraft.body);
    setAppliedAiBody(aiDraft.body);
    setExpanded(true);
  }, [wantAiDraft, aiDraft]);

  // ---- shipment documents ---------------------------------------------------

  const loadDocuments = useCallback(async () => {
    if (!alertId) return;
    try {
      setCatalog(await getEmailReplyDocuments(token, alertId));
    } catch {
      setAttachError({ kind: 'documents' });
    }
  }, [alertId, token]);

  // A saved draft only stores document ids; their names come from the list.
  useEffect(() => {
    if (documentIds.length > 0 && catalog.length === 0) {
      const _ = loadDocuments();
    }
  }, [documentIds, catalog.length, loadDocuments]);

  const documentSize = useCallback(
    (ids: string[]) =>
      ids.reduce(
        (sum, id) => sum + (catalog.find((d) => d.id === id)?.size_bytes ?? 0),
        0
      ),
    [catalog]
  );

  // ---- derived --------------------------------------------------------------

  const hasAttachments = files.length > 0 || documentIds.length > 0;
  const edited =
    (body.trim() !== '' && body !== appliedAiBody) || hasAttachments;
  const canSend = canReply && (body.trim() !== '' || hasAttachments);
  const aiLoading = wantAiDraft && aiDraft.status === 'pending';
  // Only an answer the user asked for can fail or come back empty.
  const aiNotice: 'failed' | 'none' | null = !aiRequested
    ? null
    : aiRequestFailed || aiDraft.status === 'failed'
      ? 'failed'
      : aiDraft.status === 'none'
        ? 'none'
        : null;
  // The button is for an empty box that has not been given an AI draft.
  const canDraftWithAi =
    canReply && body.trim() === '' && appliedAiBody === '' && !aiLoading;

  const attachmentNames = useMemo(
    () => [
      ...files.map((f) => f.name),
      ...documentIds.map(
        (id) => catalog.find((d) => d.id === id)?.file_name ?? 'Document'
      ),
    ],
    [files, documentIds, catalog]
  );

  // ---- autosave -------------------------------------------------------------

  const pendingSave = useRef<{ body: string; ids: string[] } | null>(null);
  const lastSavedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!canReply || !alertId) return;
    if (!edited) {
      pendingSave.current = null;
      if (savedDraftRef.current && touchedRef.current && body.trim() === '') {
        savedDraftRef.current = false;
        deleteEmailDraft(token, alertId).catch(ignoreFailure);
      }
      return;
    }
    pendingSave.current = { body, ids: documentIds };
    const timer = setTimeout(() => {
      const payload = pendingSave.current;
      pendingSave.current = null;
      if (!payload) return;
      const key = draftKey(payload.body, payload.ids);
      // A draft that was only loaded is already what the server holds.
      if (key === lastSavedKey.current) return;
      lastSavedKey.current = key;
      savedDraftRef.current = true;
      putEmailDraft(token, alertId, {
        body: payload.body,
        attachment_ids: payload.ids,
      }).catch(ignoreFailure);
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [body, documentIds, edited, canReply, alertId, token]);

  // Closing the panel inside the delay must not lose the last keystrokes.
  useEffect(
    () => () => {
      const payload = pendingSave.current;
      if (payload && alertId) {
        putEmailDraft(token, alertId, {
          body: payload.body,
          attachment_ids: payload.ids,
        }).catch(ignoreFailure);
      }
    },
    [alertId, token]
  );

  // ---- editing --------------------------------------------------------------

  const setBody = useCallback((next: string) => {
    touchedRef.current = true;
    setBodyState(next);
    setConflict(null);
    setAiRequested(false);
  }, []);

  const expand = useCallback(() => setExpanded(true), []);

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;
      const total =
        files.reduce((sum, f) => sum + f.size, 0) +
        incoming.reduce((sum, f) => sum + f.size, 0) +
        documentSize(documentIds);
      if (total > MAX_ATTACHMENT_BYTES) {
        setAttachError({
          kind: 'limit',
          mb: MAX_ATTACHMENT_BYTES / (1024 * 1024),
        });
        return;
      }
      setAttachError(null);
      touchedRef.current = true;
      setFiles((prev) => [...prev, ...incoming]);
      setBodyState((prev) => removeFirstNotAttachedYet(prev));
    },
    [files, documentIds, documentSize]
  );

  const addDocuments = useCallback(
    (ids: string[]) => {
      const added = ids.filter((id) => !documentIds.includes(id));
      if (added.length === 0) return;
      const total =
        files.reduce((sum, f) => sum + f.size, 0) +
        documentSize([...documentIds, ...added]);
      if (total > MAX_ATTACHMENT_BYTES) {
        setAttachError({
          kind: 'limit',
          mb: MAX_ATTACHMENT_BYTES / (1024 * 1024),
        });
        return;
      }
      setAttachError(null);
      touchedRef.current = true;
      setDocumentIds((prev) => [...prev, ...added]);
      setBodyState((prev) => removeFirstNotAttachedYet(prev));
    },
    [files, documentIds, documentSize]
  );

  const removeFile = useCallback(
    (index: number) => setFiles((prev) => prev.filter((_, i) => i !== index)),
    []
  );
  const removeDocument = useCallback(
    (id: string) => setDocumentIds((prev) => prev.filter((d) => d !== id)),
    []
  );

  const linkEditorRef = useRef({
    getSelectedText: (): string => {
      const ta = editorRef.current;
      return ta ? ta.value.substring(ta.selectionStart, ta.selectionEnd) : '';
    },
    insertLink: ({ href, text }: { href: string; text?: string }) => {
      const ta = editorRef.current;
      if (!ta) return;
      const insert = text ? `${text} (${href})` : href;
      setBody(
        ta.value.substring(0, ta.selectionStart) +
          insert +
          ta.value.substring(ta.selectionEnd)
      );
    },
  });

  // ---- cancel / discard -----------------------------------------------------

  const clearComposer = useCallback(() => {
    touchedRef.current = false;
    setBodyState('');
    setFiles([]);
    setDocumentIds([]);
    setAppliedAiBody('');
    setGuardCount(null);
    setAttachError(null);
    setExpanded(false);
    setWantAiDraft(false);
    setAiRequested(false);
    setAiRequestFailed(false);
  }, []);

  const discard = useCallback(() => {
    clearComposer();
    setConflict(null);
    lastSavedKey.current = null;
    if (savedDraftRef.current && alertId) {
      savedDraftRef.current = false;
      deleteEmailDraft(token, alertId).catch(ignoreFailure);
    }
  }, [clearComposer, alertId, token]);

  // ---- send -----------------------------------------------------------------

  const patchItem = useCallback(
    (localId: string, patch: Partial<OutboxItem>) =>
      setOutbox((items) =>
        items.map((item) =>
          item.localId === localId ? { ...item, ...patch } : item
        )
      ),
    []
  );

  const run = useCallback(
    async (item: OutboxItem) => {
      if (!alertId) return;
      patchItem(item.localId, { status: 'sending', error: undefined });
      try {
        const reply = await postNativeEmailReply(token, alertId, {
          body: item.body,
          clientRequestId: item.clientRequestId,
          files: item.files,
          attachmentIds: item.documentIds,
          lastSeenMessageId:
            nativeRef.current?.reply_target_thread?.last_message_id ?? null,
          threadId: nativeRef.current?.reply_target_thread?.id ?? null,
          placeholdersSent: item.placeholdersSent,
          draftedFromAi: item.aiDraftText !== '',
          aiDraftText: item.aiDraftText,
        });
        patchItem(item.localId, { status: 'sent', reply });
        // The server deletes the saved draft once the reply is sent.
        savedDraftRef.current = false;
        lastSavedKey.current = null;
        reloadTranscript().catch(ignoreFailure);
      } catch (error) {
        const failure = toReplyFailure(error);
        if (failure.code === 'newer_reply') {
          // Another user answered first: keep what was typed, say so, and
          // reload so the next Send is based on the newer thread.
          setOutbox((items) => items.filter((i) => i.localId !== item.localId));
          touchedRef.current = true;
          setBodyState(item.body);
          setAppliedAiBody(item.aiDraftText);
          setFiles(item.files);
          setDocumentIds(item.documentIds);
          setExpanded(true);
          setConflict(failure.message);
          reloadTranscript().catch(ignoreFailure);
          return;
        }
        patchItem(item.localId, { status: 'failed', error: failure.message });
      }
    },
    [alertId, token, patchItem, reloadTranscript]
  );

  const send = useCallback(
    (force = false) => {
      if (!canSend) return;
      const placeholders = countPlaceholders(body);
      if (placeholders > 0 && !force) {
        setGuardCount(placeholders);
        return;
      }
      const item: OutboxItem = {
        localId: newId(),
        clientRequestId: newId(),
        body,
        files,
        documentIds,
        attachmentNames,
        placeholdersSent: placeholders,
        aiDraftText: appliedAiBody,
        status: 'sending',
      };
      setOutbox((items) => [...items, item]);
      clearComposer();
      // The AI draft was used up by this reply.
      setAiDraft(NO_AI_DRAFT);
      setConflict(null);
      const _ = run(item);
    },
    [
      canSend,
      body,
      files,
      documentIds,
      attachmentNames,
      appliedAiBody,
      clearComposer,
      run,
    ]
  );

  const retry = useCallback(
    (localId: string) => {
      const item = outbox.find((i) => i.localId === localId);
      if (item) {
        const _ = run(item);
      }
    },
    [outbox, run]
  );

  /** Take a failed reply back into the box, to change it before sending again. */
  const restore = useCallback(
    (localId: string) => {
      const item = outbox.find((i) => i.localId === localId);
      if (!item) return;
      setOutbox((items) => items.filter((i) => i.localId !== localId));
      touchedRef.current = true;
      setBodyState(item.body);
      setAppliedAiBody(item.aiDraftText);
      setFiles(item.files);
      setDocumentIds(item.documentIds);
      setExpanded(true);
    },
    [outbox]
  );

  /**
   * Draft with AI: ask the server for the answer to this thread. It comes back
   * ready, pending (then polled every 3 s) or none. A call while one is already
   * pending changes nothing on the server.
   */
  const draftWithAi = useCallback(async () => {
    if (!canReply || !alertId) return;
    setWantAiDraft(true);
    setAiRequested(true);
    setAiRequestFailed(false);
    setExpanded(true);
    setAiDraft({ status: 'pending', body: null, generated_at: null });
    try {
      setAiDraft(await postDraftWithAi(token, alertId, threadId));
    } catch {
      setAiRequestFailed(true);
      setAiDraft(NO_AI_DRAFT);
    }
  }, [canReply, alertId, token, threadId]);

  const keepEditing = useCallback(() => {
    setGuardCount(null);
    const range = firstPlaceholder(body);
    const editor = editorRef.current;
    if (range && editor) {
      editor.focus();
      editor.setSelectionRange(range.start, range.end);
    }
  }, [body]);

  return {
    visible: canReply,
    expanded,
    expand,
    body,
    setBody,
    aiLoading,
    aiNotice,
    canDraftWithAi,
    draftWithAi,
    files,
    documentIds,
    attachmentNames,
    catalog,
    loadDocuments,
    addFiles,
    addDocuments,
    removeFile,
    removeDocument,
    attachError,
    editorRef,
    linkEditorRef,
    edited,
    canSend,
    guardCount,
    keepEditing,
    send,
    sendAnyway: () => send(true),
    discard,
    collapse: clearComposer,
    conflict,
    outbox,
    retry,
    restore,
  };
}

export type EmailComposerState = ReturnType<typeof useEmailComposer>;
